import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../AppContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import ReportSelector from "./ReportSelector";
import ReportFilters from "./ReportFilters";
import ReportContent from "./ReportContent";
import useReportData from "./hooks/useReportData";
import { allTcrFn } from "../purchases/Utils";
import "./Reports.scss";

const Reports = () => {
  const { t } = useTranslation("translations");
  const {
    user: [user],
    selectedCompany: [selectedCompany],
    company: [fullCompany],
    dates: [dates],
  } = useAppContext();

  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState({
    fromDate: dates.fromDate,
    toDate: dates.toDate,
  });
  const [businessUnit, setBusinessUnit] = useState(null);
  const [purchaseUnits, setPurchaseUnits] = useState([]);
  const [limit, setLimit] = useState(10);

  const { data, loading, error, fetchReport, clearData } = useReportData();

  const company = user.companiesRights.find(
    (c) => c.id === parseInt(selectedCompany)
  );

  const purchaseUnitOptions = useMemo(
    () => allTcrFn(fullCompany),
    [fullCompany]
  );

  const allBuCodes = useMemo(() => {
    return company?.branches?.map((branch) => branch.businUnitCode) ?? [];
  }, [company]);

  const businessUnitOptions = useMemo(() => {
    if (!company?.branches) return [];
    return [
      { label: t("common.all"), value: null },
      ...company.branches.map((branch) => ({
        label: branch.businUnitCode + " | " + branch.sellerName,
        value: branch.businUnitCode,
      })),
    ];
  }, [selectedCompany, company, t]);

  const handleSelectReport = useCallback((report) => {
    clearData();
    setPurchaseUnits([]);
    setSelectedReport(report);
  }, [clearData]);

  const handleGenerate = useCallback(() => {
    if (!selectedReport) return;
    fetchReport(selectedReport, dateRange, businessUnit, limit, allBuCodes, t, purchaseUnits);
  }, [selectedReport, dateRange, businessUnit, limit, fetchReport, allBuCodes, t, purchaseUnits]);

  const handleBack = useCallback(() => {
    clearData();
    setPurchaseUnits([]);
    setSelectedReport(null);
  }, [clearData]);

  return (
    <div className="reports">
      <div className="reports__header">
        <div className="reports__header-left">
          {selectedReport && (
            <button className="reports__back-btn" onClick={handleBack}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          )}
          <h1 className="reports__title">
            {selectedReport ? t(selectedReport.titleKey) : t("reports.title")}
          </h1>
        </div>
      </div>

      {!selectedReport ? (
        <ReportSelector
          selectedReport={selectedReport}
          onSelectReport={handleSelectReport}
        />
      ) : (
        <>
          <ReportFilters
            dateRange={dateRange}
            onDateChange={setDateRange}
            businessUnits={businessUnitOptions}
            selectedBusinessUnit={businessUnit}
            onBusinessUnitChange={setBusinessUnit}
            purchaseUnitOptions={purchaseUnitOptions}
            selectedPurchaseUnits={purchaseUnits}
            onPurchaseUnitsChange={setPurchaseUnits}
            onGenerate={handleGenerate}
            loading={loading}
            reportConfig={selectedReport}
            limit={limit}
            onLimitChange={setLimit}
          />

          {error && (
            <div className="reports__error">
              <p>{error}</p>
            </div>
          )}

          <ReportContent
            data={data}
            config={selectedReport}
            dateRange={dateRange}
            companyName={company?.name}
            loading={loading}
          />
        </>
      )}
    </div>
  );
};

export default Reports;
